import {
  Route,
  Navigate,
  redirect,
  createRoutesFromElements,
  createBrowserRouter,
  RouterProvider,
  Outlet,
} from 'react-router-dom';

import '@/css/style.css';

// Import pages

import GroupSettings from '@/pages/group/group-settings';

import UserSettings from '@/pages/user/user-settings';
import UserGeneralPanel from '@/partials/user/settings/general/general-panel';
import GroupsPanel from '@/partials/user/settings/groups/groups-panel';

import NotFound from '@/pages/utility/not-found';
import { payoutSelectionRedirectLoader } from './features/payout-accounts/utils/payout-selection-redirect-loader';
import ModalRoot from '@/components/modal/ModalRoot';
import Sale from '@/pages/sale/sale';
import Sales from '@/pages/sale/sales';
import { SaleWrapper } from "./features/sales/components/sale-wrapper";

import GeneralPanel from '@/partials/group/settings/general/general-panel';
import Authorize from '@/components/auth/authorize';
import { MembershipRole } from './types/api/membership';
import Unauthorized from './pages/utility/unauthorized';
import SecurityPanel from './partials/user/settings/security/security-panel';
import { WorkInProgress } from './pages/utility/work-in-progress';
import { AuthLayout } from './partials/shared/layouts/auth-layout';
import { BankIdPage } from './pages/auth/bankid';
import { ContextPage } from './pages/auth/context';
import { DashboardLayout } from '@/shared/components/layout/dashboard/dashboard-layout';
import { useAuthStore } from './stores/auth.store';
import { AppSplash } from './components/app-splash';
import { ensureSessionRefreshed } from './lib/auth';
import { ToastContainer } from 'react-toastify';
import { RequestOtp } from './features/auth/pages/request-otp';
import { VerifyOtp } from './features/auth/pages/verify-otp';
import { Register } from './features/auth/pages/register';
import { RegisterGroup } from './features/groups/pages/register-group';
import { GroupLayout } from './features/groups/component/group-layout';
import { GroupRegistered } from './features/groups/pages/group-registered';
import { CreateSale } from './features/sales/pages/create-sale';
import { Protected } from './components/auth/protected';
import { RegisterMembership } from './features/memberships/pages/register-membership';
import { MembershipRegistered } from './features/memberships/pages/membership-registered';
import { PayoutAccounts } from './features/payout-accounts/pages/payout-accounts';
import { Participations } from './features/participations/pages/participations';
import { paths } from './config/paths';
import { Orders } from './features/orders/pages/orders';
import { RouterErrorFallback } from './shared/components/errors/router';
import { Memberships } from './features/memberships/pages/memberships';
import { resolveGroupEntryPath } from './lib/group-entry';
import { GroupOnboarding } from './features/groups/pages/group-onboarding';
import { NotFoundFallback } from './shared/components/errors/not-found';
import { OnboardingGuard } from './features/groups/component/onboarding-guard';
import { ErrorFrame } from './shared/components/error-frame';
import { usePendingBonusCodeStore } from './features/bonus-codes/stores/pending-bonus-code.store';
import { normalizeBonusCode } from './features/bonus-codes/api/check-bonus-code';

async function indexLoader({ request }: { request: Request }) {
  const url = new URL(request.url);

  if (!useAuthStore.getState().isAuthenticated) {
    await ensureSessionRefreshed();
  }

  const { isAuthenticated, context } = useAuthStore.getState();

  if (!isAuthenticated) throw redirect(paths.auth.login.getHref(url.pathname));

  if (!context?.groupId) throw redirect(paths.auth.context.getHref());

  throw redirect(await resolveGroupEntryPath(context.groupId));
}

/** A shared "Tipsa en kompis" link: keep the code and send the friend on to start a sale with it. */
async function startLoader({ request }: { request: Request }) {
  const code = new URL(request.url).searchParams.get('code');
  if (code?.trim()) usePendingBonusCodeStore.getState().setCode(normalizeBonusCode(code));

  if (!useAuthStore.getState().isAuthenticated) {
    await ensureSessionRefreshed();
  }

  const { isAuthenticated, context } = useAuthStore.getState();

  // A new friend registers a group, and the onboarding's create sale step has the code filled in.
  if (!isAuthenticated) throw redirect(paths.auth.register.getHref(paths.groups.register.getHref()));
  if (!context?.groupId) throw redirect(paths.groups.register.getHref());
  if (context.membershipRoles.includes(MembershipRole.Leader)) throw redirect(paths.sales.create.getHref());

  throw redirect(await resolveGroupEntryPath(context.groupId));
}

async function protectedLoader({ request }: { request: Request }) {
  const isAuthenticated = useAuthStore.getState().isAuthenticated;

  if (!isAuthenticated) {
    await ensureSessionRefreshed();
  }

  if (!useAuthStore.getState().isAuthenticated) {
    const url = new URL(request.url);
    // Keep the query too, e.g. the website's profit calculator values on /groups/register
    const from = url.pathname + url.search;

    if (
      url.pathname === paths.groups.register.getHref() ||
      url.pathname.startsWith(paths.memberships.register.getHref(''))
    ) {
      throw redirect(paths.auth.register.getHref(from));
    }

    throw redirect(paths.auth.login.getHref(from));
  }

  return null;
}

async function payoutLoader({ request }: { request: Request }) {
  const isAuthenticated = useAuthStore.getState().isAuthenticated;

  if (!isAuthenticated) {
    await ensureSessionRefreshed();
  }

  const bankid = new URL(request.url).searchParams.get('bankid');

  const { context } = useAuthStore.getState();

  const age = context?.age;

  if (age === undefined) {
    await ensureSessionRefreshed();
  }

  if (age === null && bankid === 'done') {
    await new Promise((r) => setTimeout(r, 500));
    await ensureSessionRefreshed();
  }

  return null;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      errorElement={<RouterErrorFallback />}
      element={
        <>
          <Outlet />
          <ModalRoot />
          <ToastContainer />
        </>
      }
    >
      <Route index loader={indexLoader} />
      <Route path="/start" loader={startLoader} />

      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<RequestOtp />} />
        <Route path="register" element={<Register />} />
        <Route path="verify" element={<VerifyOtp />} />
        <Route element={<Protected />} loader={protectedLoader}>
          <Route path="bankid" element={<BankIdPage />} />
          <Route path="context" element={<ContextPage />} />
        </Route>
      </Route>

      <Route element={<Protected />} loader={protectedLoader}>
        <Route path="/invite" element={<GroupLayout />}>
          <Route path=":code" element={<RegisterMembership />} />
        </Route>
        <Route path="/groups" element={<GroupLayout />}>
          <Route path="register" element={<RegisterGroup />} />
          <Route path=":groupId/registered" element={<GroupRegistered />} />
          <Route path=":groupId/memberships/registered" element={<MembershipRegistered />} />
        </Route>

        <Route element={<DashboardLayout />}>
          <Route element={<Authorize />}>
            <Route path="/sales">
              <Route element={<Authorize role={MembershipRole.Leader} />}>
                <Route path="create" element={<CreateSale />} />
              </Route>
              <Route element={<OnboardingGuard />}>
                <Route index element={<Sales />} />
                <Route path=":saleId" >
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<Sale />} />
                  <Route path="participations" element={<Participations />} />
                  <Route path="orders" element={<Orders />} />
                </Route>
              </Route>
            </Route>

            <Route path="/group" element={<Outlet />}>
              <Route path="onboarding" element={<GroupOnboarding />} loader={payoutLoader} />
              <Route element={<Authorize role={MembershipRole.Leader} />}>
                <Route path="memberships" element={<Memberships />} />
                <Route path="settings" element={<GroupSettings />}>
                  <Route index element={<Navigate to="general" replace />} />
                  <Route path="general" element={<GeneralPanel />} />
                  <Route path="payout" element={<PayoutAccounts />} loader={payoutLoader} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="/user" element={<UserSettings />}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<UserGeneralPanel />} />
            <Route path="groups" element={<GroupsPanel />} />
            <Route path="security" element={<SecurityPanel />} />
          </Route>
        </Route>
        <Route path="/payout-selection-callback" loader={payoutSelectionRedirectLoader} />
      </Route>

      <Route path="/callback" loader={payoutSelectionRedirectLoader} />

      <Route path="/work-in-progress" element={<WorkInProgress />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      <Route path="*" element={<NotFoundFallback />} />
    </Route>,
  ),
);

export function AppRouter() {
  return <RouterProvider router={router} fallbackElement={<AppSplash />} />;
}
